/**
 * The page numbers, shared by every screen that has them.
 *
 * This lived inside Market Data, which was right while Market Data was the
 * only screen with numbered pages. Benchmarking has them too now, and two
 * copies of it is how one screen starts offering a page that does not exist
 * while the other does not.
 *
 * Numbers rather than one more button, because "load more" pressed eleven
 * times leaves a person scrolling through four hundred rows to find where
 * they were, with no way to say which page they are on or to go back to it.
 *
 * At most a handful are drawn whatever the count: 1, a window around the page
 * in hand, and the last one, with a gap where numbers were left out. Fourteen
 * numbers in a row is a wall, and a hundred thousand rows would be two and a
 * half thousand of them.
 */
export function PageNumbers({
  at,
  pages,
  open,
  busy,
  onGo,
}: {
  at: number
  pages: number
  /** True while there is more to read past the last numbered page. */
  open: boolean
  busy: boolean
  onGo: (n: number) => void
}) {
  if (pages <= 1) return null

  const shown = new Set<number>([0, pages - 1, at])
  for (let n = at - 1; n <= at + 1; n++) if (n >= 0 && n < pages) shown.add(n)
  const numbers = [...shown].sort((a, b) => a - b)

  return (
    <div className="pages">
      {/* A jump to a page nobody has been to reads the pages in front of it,
          which takes a moment. Without this the buttons simply went dead. */}
      {busy && <span className="pages__busy">Loading…</span>}
      <button
        type="button"
        className="btn btn--sm"
        onClick={() => onGo(at - 1)}
        disabled={at === 0 || busy}
        aria-label="Previous page"
      >
        ←
      </button>
      {numbers.map((n, i) => (
        <span key={n} className="pages__slot">
          {/* A gap stands for the numbers between, which are still reachable
              by walking there one page at a time or by using the last one. */}
          {i > 0 && n - numbers[i - 1] > 1 && <span className="pages__gap">…</span>}
          <button
            type="button"
            className={n === at ? 'btn btn--sm btn--page is-here' : 'btn btn--sm btn--page'}
            onClick={() => onGo(n)}
            disabled={busy}
            aria-current={n === at ? 'page' : undefined}
          >
            {n + 1}
          </button>
        </span>
      ))}
      {/* Where the count is not known, the last number is only the furthest
          anybody has been, so it is marked as not being the end. It is dropped
          the moment a page comes back saying there is nothing after it. */}
      {open && <span className="pages__gap">…</span>}
      <button
        type="button"
        className="btn btn--sm"
        onClick={() => onGo(at + 1)}
        disabled={at >= pages - 1 || busy}
        aria-label="Next page"
      >
        →
      </button>
    </div>
  )
}
